package fr.frux.feature.map

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.HelpOutline
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxModalBottomSheet
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.SpeciesFamily
import fr.frux.core.designsystem.icons.hybridMarkerTint
import fr.frux.core.designsystem.icons.legendColor
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.designsystem.theme.label
import fr.frux.core.maps.ClusterSample
import fr.frux.core.maps.NotchedMarkerShape
import fr.frux.core.maps.TreeMarkerSample
import fr.frux.core.maps.clusterSampleSize
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_legend_cluster
import fr.frux.feature.map.resources.map_legend_content_description
import fr.frux.feature.map.resources.map_legend_families_heading
import fr.frux.feature.map.resources.map_legend_grey_statuses
import fr.frux.feature.map.resources.map_legend_intensity_heading
import fr.frux.feature.map.resources.map_legend_notched_description
import fr.frux.feature.map.resources.map_legend_shapes_heading
import fr.frux.feature.map.resources.map_legend_subtitle
import fr.frux.feature.map.resources.map_legend_title
import fr.frux.feature.map.resources.map_legend_tree_marker
import fr.frux.feature.map.site.siteLegendSection
import org.jetbrains.compose.resources.stringResource

/** Empreinte du bouton legende (#133) : plus discret que les actions carte, pas une commande. */
private val MAP_LEGEND_BUTTON_SIZE = 40.dp

/**
 * Bouton « ? » ouvrant la legende du code couleur des marqueurs (#133). Meme famille visuelle que
 * [LocateMeButton] (Surface, cercle, meme elevation) pour lire comme la meme colonne de controles
 * carte, mais plus petit : ce n'est pas une action, seulement une aide contextuelle.
 */
@Composable
internal fun MapLegendButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        modifier = modifier.size(MAP_LEGEND_BUTTON_SIZE),
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.primary,
        shadowElevation = 2.dp,
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(
                imageVector = Icons.Outlined.HelpOutline,
                contentDescription = stringResource(Res.string.map_legend_content_description),
                modifier = Modifier.size(20.dp),
            )
        }
    }
}

/**
 * Legende du code couleur des marqueurs (#133). Le contexte a change depuis la redaction de
 * l'issue (qui decrivait encore une teinte par partie recoltee, #196/#204 l'ont depuis remplacee) :
 * les echantillons ci-dessous REFERENCENT les memes fonctions/tokens que [toMapMarkers] --
 * [speciesFamilyColor]/[SpeciesFamily.legendColor] pour les familles, [hybridMarkerTint] pour le
 * traitement plein/pale/gris -- au lieu de recopier des valeurs, pour qu'un changement de teinte
 * se reflete ici sans double maintenance.
 *
 * En tete, un arbre seul et un groupe (#710) : le glyphe d'espece apparait sur les deux, la
 * legende dit d'abord lequel est lequel. Les echantillons viennent de core-maps
 * ([TreeMarkerSample], [ClusterSample]) pour garder la geometrie reelle.
 *
 * Aucun cas « partie inconnue = gris » : mesure a 0% en donnee (#134), il n'existe plus. Le gris de
 * cette legende est un statut (cueilli / hors saison / inaccessible / inconnu), pas une famille
 * absente.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun MapLegendSheet(onDismiss: () -> Unit) {
    val colors = MaterialTheme.fruxColors
    val legendDescription = stringResource(Res.string.map_legend_content_description)
    FruxModalBottomSheet(onDismiss = onDismiss, skipPartiallyExpanded = true) {
        LazyColumn(
            contentPadding = PaddingValues(horizontal = FruxSpacing.md, vertical = FruxSpacing.md),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
            modifier = Modifier.semantics { contentDescription = legendDescription },
        ) {
            item {
                Text(
                    text = stringResource(Res.string.map_legend_title),
                    style = MaterialTheme.typography.titleMedium,
                )
                Spacer(modifier = Modifier.height(FruxSpacing.xs))
                Text(
                    text = stringResource(Res.string.map_legend_subtitle),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            shapesLegendSection()

            item {
                Spacer(modifier = Modifier.height(FruxSpacing.sm))
                Text(
                    text = stringResource(Res.string.map_legend_families_heading),
                    style = MaterialTheme.typography.labelLarge,
                )
            }

            // Exhaustif par construction (#133) : `when` non-exhaustif = erreur de compilation si
            // SpeciesFamily gagne une valeur. Vérifié aussi côté test (couverture explicite).
            items(SpeciesFamily.entries.toList()) { family ->
                FamilyRow(
                    color = family.legendColor(colors),
                    glyphs = family.exampleGlyphs(),
                    label = family.legendLabel(),
                )
            }

            item {
                Spacer(modifier = Modifier.height(FruxSpacing.sm))
                Text(
                    text = stringResource(Res.string.map_legend_intensity_heading),
                    style = MaterialTheme.typography.labelLarge,
                )
            }

            item {
                LegendRow(
                    color = hybridMarkerTint(colors.familyPomme, FruxStatus.Available, colors),
                    label = FruxStatus.Available.label,
                )
            }
            item {
                LegendRow(
                    color = hybridMarkerTint(colors.familyPomme, FruxStatus.Flowering, colors),
                    label = FruxStatus.Flowering.label,
                )
            }
            item {
                LegendRow(
                    color = hybridMarkerTint(colors.familyPomme, FruxStatus.Picked, colors),
                    label =
                        stringResource(
                            Res.string.map_legend_grey_statuses,
                            GREY_STATUSES.dropLast(1).joinToString(", ") { it.label },
                            GREY_STATUSES.last().label,
                        ),
                )
            }

            item {
                Spacer(modifier = Modifier.height(FruxSpacing.sm))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    // Même géométrie que le marqueur : la légende montre la denture réelle plutôt
                    // qu'une approximation qui divergerait au premier réglage du sprite.
                    Surface(
                        modifier = Modifier.size(20.dp),
                        shape = NotchedMarkerShape,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    ) {}
                    Spacer(modifier = Modifier.width(FruxSpacing.sm))
                    Text(
                        text = stringResource(Res.string.map_legend_notched_description),
                        style = MaterialTheme.typography.bodyMedium,
                    )
                }
            }

            siteLegendSection()

            item { Spacer(modifier = Modifier.height(FruxSpacing.md)) }
        }
    }
}

/** Statuts qui se confondent en gris neutre sur la carte (cf. [hybridMarkerTint]). */
private val GREY_STATUSES =
    listOf(
        FruxStatus.Picked,
        FruxStatus.OffSeason,
        FruxStatus.Unavailable,
        FruxStatus.Unknown,
    )

/** Un arbre seul et un groupe (#710) : le glyphe d'espece apparait sur les deux. */
private fun LazyListScope.shapesLegendSection() {
    item {
        Spacer(modifier = Modifier.height(FruxSpacing.sm))
        Text(
            text = stringResource(Res.string.map_legend_shapes_heading),
            style = MaterialTheme.typography.labelLarge,
        )
    }

    item {
        ShapeRow(label = stringResource(Res.string.map_legend_tree_marker)) {
            TreeMarkerSample(
                glyph = FruxSpeciesGlyphs.Pomme,
                tint = MaterialTheme.fruxColors.familyPomme,
                scale = SAMPLE_SCALE,
            )
        }
    }

    item {
        ShapeRow(label = stringResource(Res.string.map_legend_cluster)) {
            val colors = MaterialTheme.fruxColors
            ClusterSample(
                count = 12,
                satellites =
                    listOf(
                        FruxSpeciesGlyphs.Pomme to colors.familyPomme,
                        FruxSpeciesGlyphs.Cerise to colors.familyRouge,
                        FruxSpeciesGlyphs.Noix to colors.familyCoque,
                        FruxSpeciesGlyphs.Prune to colors.familyPrune,
                    ),
                scale = SAMPLE_SCALE,
            )
        }
    }
}

/** Echantillons reduits pour tenir dans une ligne de feuille sans ecraser le texte. */
private const val SAMPLE_SCALE = 0.75f

/** Deux glyphes au plus par famille : assez pour dire « une teinte, plusieurs especes ». */
private fun SpeciesFamily.exampleGlyphs(): List<ImageVector> =
    when (this) {
        SpeciesFamily.POMME_POIRE -> listOf(FruxSpeciesGlyphs.Pomme, FruxSpeciesGlyphs.Poire)
        SpeciesFamily.ROUGE -> listOf(FruxSpeciesGlyphs.Cerise, FruxSpeciesGlyphs.Framboise)
        SpeciesFamily.DORE -> listOf(FruxSpeciesGlyphs.Abricot, FruxSpeciesGlyphs.Peche)
        SpeciesFamily.PRUNE_BAIES -> listOf(FruxSpeciesGlyphs.Prune, FruxSpeciesGlyphs.Mure)
        SpeciesFamily.COQUE -> listOf(FruxSpeciesGlyphs.Noix, FruxSpeciesGlyphs.Chataigne)
        SpeciesFamily.AUTRE -> listOf(FruxSpeciesGlyphs.Figue, FruxSpeciesGlyphs.Feuille)
    }

private val FAMILY_GLYPH_SIZE = 24.dp

@Composable
private fun FamilyRow(
    color: Color,
    glyphs: List<ImageVector>,
    label: String,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.semantics(mergeDescendants = true) {},
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
            glyphs.forEach { glyph -> TreeGlyphDisc(glyph = glyph, tint = color, size = FAMILY_GLYPH_SIZE) }
        }
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Text(text = label, style = MaterialTheme.typography.bodyMedium)
    }
}

/** L'echantillon occupe la largeur de la couronne complete, pour aligner les deux textes. */
@Composable
private fun ShapeRow(
    label: String,
    sample: @Composable () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.semantics(mergeDescendants = true) {},
    ) {
        Box(modifier = Modifier.width(clusterSampleSize(SAMPLE_SCALE)), contentAlignment = Alignment.Center) {
            sample()
        }
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Text(text = label, style = MaterialTheme.typography.bodyMedium)
    }
}

@Composable
private fun LegendRow(
    color: Color,
    label: String,
    modifier: Modifier = Modifier,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = modifier.semantics(mergeDescendants = true) {},
    ) {
        Surface(
            modifier = Modifier.size(20.dp),
            shape = CircleShape,
            color = color,
        ) {}
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Text(text = label, style = MaterialTheme.typography.bodyMedium)
    }
}
